/**
 * "图像"菜单的破坏性调整动作（DESIGN.md §6.5 补充，参照 Photoshop）。
 *
 * 跟"图层→新建调整图层"（adjustment-actions.ts）是两条完全不同的路：
 * 那边新建一个非破坏性的调整图层对象，走 CommandBus 结构命令；这里直接
 * 改活动图层像素（`app/color-adjust.ts`），跟画笔/填充/选区编辑同一套
 * "PixelTransaction 直接改 TileStore、不走 CommandBus"的既有约定
 * （CLAUDE.md 规则 5 管的是**文档结构**改动只能走 dispatch，像素数据的
 * 归属是 TileStore，见规则 6；`edit-actions.ts` 的「清除」是同一个先例）。
 *
 * 反相/去色是即时动作（真实 PS 也没有对话框）；色阶/曲线/曝光度/
 * 色相饱和度需要参数，`run` 只负责通知装配层开对应对话框——真正落地在
 * 对话框的「确定」回调里调 `app/color-adjust.ts` 的函数。
 */
import type { ActionRegistry } from './registry';
import type { AppStore } from '../store';
import { desaturateColors, invertColors } from '../color-adjust';

export interface ImageActionDeps {
  readonly store: AppStore;
  readonly onRequestLevels: () => void;
  readonly onRequestCurves: () => void;
  readonly onRequestExposure: () => void;
  readonly onRequestHueSaturation: () => void;
  readonly onRequestCanvasSize: () => void;
  readonly onRequestImageSize: () => void;
}

export function registerImageActions(registry: ActionRegistry, deps: ImageActionDeps): void {
  registry.register({
    id: 'image.invert',
    title: '反相',
    group: '图像',
    section: 1,
    order: 0,
    shortcut: { key: 'I', mod: true },
    enabled: (s) => s.hasDocument,
    run: (services) => {
      if (!invertColors(deps.store)) services.hint('没有可调整的图层');
    },
  });

  registry.register({
    id: 'image.desaturate',
    title: '去色',
    group: '图像',
    section: 1,
    order: 1,
    shortcut: { key: 'U', mod: true, shift: true },
    enabled: (s) => s.hasDocument,
    run: (services) => {
      if (!desaturateColors(deps.store)) services.hint('没有可调整的图层');
    },
  });

  registry.register({
    id: 'image.levels',
    title: '色阶…',
    group: '图像',
    section: 0,
    order: 0,
    shortcut: { key: 'L', mod: true },
    enabled: (s) => s.hasDocument,
    run: () => {
      deps.onRequestLevels();
    },
  });

  registry.register({
    id: 'image.curves',
    title: '曲线…',
    group: '图像',
    section: 0,
    order: 1,
    shortcut: { key: 'M', mod: true },
    enabled: (s) => s.hasDocument,
    run: () => {
      deps.onRequestCurves();
    },
  });

  registry.register({
    id: 'image.exposure',
    title: '曝光度…',
    group: '图像',
    section: 0,
    order: 2,
    enabled: (s) => s.hasDocument,
    run: () => {
      deps.onRequestExposure();
    },
  });

  registry.register({
    id: 'image.hueSaturation',
    title: '色相/饱和度…',
    group: '图像',
    section: 0,
    order: 3,
    shortcut: { key: 'U', mod: true },
    enabled: (s) => s.hasDocument,
    run: () => {
      deps.onRequestHueSaturation();
    },
  });

  registry.register({
    id: 'image.imageSize',
    title: '图像大小…',
    group: '图像',
    section: 2,
    order: 0,
    shortcut: { key: 'I', mod: true, alt: true },
    enabled: (s) => s.hasDocument,
    run: () => {
      deps.onRequestImageSize();
    },
  });

  registry.register({
    id: 'image.canvasSize',
    title: '画布大小…',
    group: '图像',
    section: 2,
    order: 1,
    shortcut: { key: 'C', mod: true, alt: true },
    enabled: (s) => s.hasDocument,
    run: () => {
      deps.onRequestCanvasSize();
    },
  });
}
